/* Tagomago: a single navigation tree, progressively enhanced.
 * No dependencies, innerHTML, clones, fetches, or duplicate device templates.
 * The native modal dialog escapes transformed sticky-header ancestors.
 */
(() => {
  'use strict';
  if (customElements.get('tagomago-navigation')) return;
  const DESKTOP_QUERY = '(min-width: 992px)'; // Must match CSS.

  class TagomagoNavigation extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      // A registered custom element can connect before its children are parsed.
      // Observe only until the server-rendered component is complete.
      this.setupObserver?.disconnect();
      this.setupObserver = new MutationObserver(() => this.initialize());
      this.setupObserver.observe(this, { childList: true, subtree: true });
      this.initialize();
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      this.home = this.querySelector('[data-tg-nav-home]');
      this.nav = this.querySelector('[data-tg-navigation]');
      this.dialog = this.querySelector('[data-tg-nav-dialog]');
      this.closeButton = this.querySelector('[data-tg-nav-close]');
      this.trigger = Array.from(document.querySelectorAll('[data-tg-menu-toggle]'))
        .find(button => button.dataset.tgMenuToggle === this.dataset.sectionId);
      // Without dialog support leave the server-rendered expanded navigation intact.
      if (!this.home || !this.nav || !this.dialog || !this.closeButton || !this.trigger) return;
      if (typeof this.dialog.showModal !== 'function') {
        this.setupObserver?.disconnect();
        this.setupObserver = null;
        return;
      }

      this.setupObserver?.disconnect();
      this.setupObserver = null;
      this.initialized = true;
      this.controller = new AbortController();
      this.media = window.matchMedia(DESKTOP_QUERY);
      this.buttons = Array.from(this.nav.querySelectorAll('[data-tg-submenu-toggle]'));
      this.panels = new Map();
      this.buttons.forEach(button => {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (panel && this.nav.contains(panel)) this.panels.set(button, panel);
      });
      this.mainRow = this.nav.querySelector('.tg-nav__row--main');
      this.auxRow = this.nav.querySelector('.tg-nav__row--aux');
      this.orderedLists = Array.from(this.nav.querySelectorAll('.tg-nav__items--main, .tg-nav__items--level-2'))
        .map(list => ({ list, items: Array.from(list.children) }));

      const listen = (element, event, handler, options = {}) => {
        element.addEventListener(event, handler, { ...options, signal: this.controller.signal });
      };
      listen(this.trigger, 'click', () => this.openDrawer());
      listen(this.closeButton, 'click', () => this.closeDrawer(true));
      listen(this.dialog, 'cancel', event => {
        event.preventDefault();
        this.closeDrawer(true);
      });
      listen(this.dialog, 'close', () => {
        // An old queued close event must not close a newly re-opened dialog.
        if (!this.dialog.open) this.finishClose();
      });
      listen(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right ||
            event.clientY < bounds.top || event.clientY > bounds.bottom) {
          this.closeDrawer(true);
        }
      });
      listen(this.nav, 'click', event => this.onClick(event));
      listen(this.nav, 'keydown', event => this.onKeyDown(event));
      listen(this.nav, 'pointerover', event => this.onPointerOver(event));
      listen(this.nav, 'pointerout', event => this.onPointerOut(event));
      listen(this.nav, 'focusout', () => {
        queueMicrotask(() => {
          if (!this.isConnected || !this.media.matches) return;
          this.rootButtons().forEach(button => {
            if (!button.closest('[data-tg-item]').contains(document.activeElement)) this.setExpanded(button, false);
          });
        });
      });
      listen(document, 'pointerdown', event => {
        if (this.media.matches && !this.contains(event.target)) this.closeSubmenus();
      });
      listen(window, 'resize', () => this.scheduleMeasure(), { passive: true });
      listen(window, 'scroll', () => this.scheduleMeasure(), { passive: true });
      listen(this.media, 'change', () => this.syncMode());
      // Custom elements reconnect automatically when Shopify reloads a section.
      listen(document, 'shopify:section:unload', event => {
        if (event.target.contains(this)) this.closeDrawer(false);
      });
      this.classList.add('tg-nav--ready');
      this.syncMode();
    }

    disconnectedCallback() {
      this.setupObserver?.disconnect();
      this.setupObserver = null;
      if (!this.initialized) return;
      this.closeDrawer(false);
      this.controller.abort();
      this.clearHover();
      cancelAnimationFrame(this.measureFrame);
      // Restore the accessible, no-JS shape before possible reconnection.
      this.home.append(this.nav);
      this.panels.forEach((panel, button) => {
        panel.hidden = false;
        panel.style.removeProperty('--tg-nav-space');
        button.hidden = true;
        button.setAttribute('aria-expanded', 'true');
      });
      this.orderedLists.forEach(({ list, items }) => items.forEach(item => list.append(item)));
      if (this.auxRow && this.mainRow) this.nav.insertBefore(this.auxRow, this.mainRow);
      this.trigger.hidden = true;
      this.classList.remove('tg-nav--ready');
      this.initialized = false;
    }

    rootButtons() {
      return this.buttons.filter(button => button.closest('[data-tg-item]').dataset.level === '1');
    }

    syncMode() {
      const desktop = this.media.matches;
      const active = document.activeElement;
      const focusWasInNav = this.nav.contains(active);
      const focusWasInDialog = this.dialog.contains(active);
      const rootItem = active?.closest?.('.tg-nav__item--level-1');
      const desktopFocus = rootItem?.querySelector('.tg-nav__item-row a, .tg-nav__item-row button');
      this.clearHover();
      this.closeDrawer(false);
      this.dataset.mode = desktop ? 'desktop' : 'mobile';
      // append() MOVES the exact existing node. No links are duplicated.
      (desktop ? this.home : this.dialog).append(this.nav);
      this.trigger.hidden = desktop;
      this.buttons.forEach(button => {
        const isRoot = button.closest('[data-tg-item]').dataset.level === '1';
        button.hidden = desktop && !isRoot;
        this.setExpanded(button, desktop && !isRoot, false);
        this.panels.get(button)?.style.removeProperty('--tg-nav-space');
      });
      // Preserve the source theme's visual order without a CSS/keyboard-order mismatch.
      if (this.mainRow && this.auxRow) {
        this.nav.insertBefore(desktop ? this.auxRow : this.mainRow, desktop ? this.mainRow : this.auxRow);
      }
      this.orderedLists.forEach(({ list, items }) => {
        const key = list.classList.contains('tg-nav__items--main') ? 'tg-nav__item--promo' : 'tg-nav__item--featured';
        let ordered = items;
        if (!desktop) {
          const first = items.filter(item => item.classList.contains(key));
          const rest = items.filter(item => !item.classList.contains(key));
          ordered = key === 'tg-nav__item--promo' ? [...first, ...rest] : [...rest, ...first];
        }
        ordered.forEach(item => list.append(item));
      });
      if (desktop && (focusWasInDialog || active === this.trigger)) {
        const target = desktopFocus || this.nav.querySelector('a, button:not([hidden])');
        target?.focus({ preventScroll: true });
      } else if (!desktop && (focusWasInNav || focusWasInDialog)) {
        this.trigger.focus({ preventScroll: true });
      }
    }

    setExpanded(button, expanded, closeDescendants = true) {
      const panel = this.panels.get(button);
      if (!panel) return;
      button.setAttribute('aria-expanded', String(expanded));
      panel.hidden = !expanded;
      if (!expanded && closeDescendants && !this.media.matches) {
        panel.querySelectorAll('[data-tg-submenu-toggle]').forEach(child => this.setExpanded(child, false, false));
      }
      if (expanded && this.media.matches) this.scheduleMeasure();
    }

    closeSubmenus() {
      this.clearHover();
      this.rootButtons().forEach(button => this.setExpanded(button, false));
    }

    onClick(event) {
      const button = event.target.closest('[data-tg-submenu-toggle]');
      if (button && this.nav.contains(button)) {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        const isRoot = button.closest('[data-tg-item]').dataset.level === '1';
        this.clearHover();
        if (isRoot) this.rootButtons().forEach(other => {
          if (other !== button) this.setExpanded(other, false);
        });
        this.setExpanded(button, !expanded);
        return;
      }
      // Keep actual <a> navigation, new tabs, tracking and Shopify routes unchanged.
      if (event.target.closest('a[href]') && !this.media.matches &&
          !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        this.closeDrawer(false);
      }
    }

    onKeyDown(event) {
      if (event.key !== 'Escape') return;
      let item = event.target.closest('[data-tg-item]');
      while (item && this.nav.contains(item)) {
        const button = item.querySelector(':scope > .tg-nav__item-row > [data-tg-submenu-toggle]');
        if (button && !button.hidden && button.getAttribute('aria-expanded') === 'true') {
          event.preventDefault();
          event.stopPropagation();
          this.clearHover();
          this.setExpanded(button, false);
          button.focus({ preventScroll: true });
          return;
        }
        item = item.parentElement.closest('[data-tg-item]');
      }
      // With no expanded ancestor, the dialog's native cancel event handles Escape.
    }

    onPointerOver(event) {
      if (!this.media.matches || event.pointerType !== 'mouse') return;
      const item = event.target.closest('.tg-nav__item--level-1');
      if (!item || item.contains(event.relatedTarget)) return;
      this.clearHover();
      const button = item.querySelector(':scope > .tg-nav__item-row > [data-tg-submenu-toggle]');
      const focusedPanel = document.activeElement?.closest?.('[data-tg-submenu]');
      if (focusedPanel && this.nav.contains(focusedPanel) && !item.contains(focusedPanel)) return;
      this.hoverTimer = window.setTimeout(() => {
        this.rootButtons().forEach(other => this.setExpanded(other, other === button));
      }, 120);
    }

    onPointerOut(event) {
      if (!this.media.matches || event.pointerType !== 'mouse') return;
      const item = event.target.closest('.tg-nav__item--level-1');
      if (!item || item.contains(event.relatedTarget)) return;
      this.clearHover();
      this.hoverTimer = window.setTimeout(() => {
        if (item.contains(document.activeElement)) return;
        const button = item.querySelector(':scope > .tg-nav__item-row > [data-tg-submenu-toggle]');
        if (button) this.setExpanded(button, false);
      }, 220);
    }

    clearHover() { window.clearTimeout(this.hoverTimer); }

    scheduleMeasure() {
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = requestAnimationFrame(() => {
        if (!this.media.matches || !this.isConnected) return;
        this.rootButtons().forEach(button => {
          const panel = this.panels.get(button);
          if (panel && !panel.hidden) {
            const available = Math.max(100, window.innerHeight - panel.getBoundingClientRect().top - 12);
            panel.style.setProperty('--tg-nav-space', `${available}px`);
          }
        });
      });
    }

    openDrawer() {
      if (this.media.matches || this.dialog.open) return;
      // Avoid overlapping body-scroll locks in an editor with multiple instances.
      document.querySelectorAll('tagomago-navigation').forEach(menu => {
        if (menu !== this && typeof menu.closeDrawer === 'function') menu.closeDrawer(false);
      });
      this.clearHover();
      this.dialog.showModal();
      this.lockScroll();
      this.trigger.setAttribute('aria-expanded', 'true');
      this.closeButton.focus({ preventScroll: true });
    }

    closeDrawer(restoreFocus = false) {
      if (!this.dialog) return;
      const wasOpen = this.dialog.open;
      if (wasOpen) this.dialog.close();
      this.finishClose();
      if (wasOpen && restoreFocus && !this.media.matches && this.trigger?.isConnected) {
        this.trigger.focus({ preventScroll: true });
      }
    }

    finishClose() {
      this.unlockScroll();
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (!this.media?.matches) this.closeSubmenus();
    }

    lockScroll() {
      if (this.scrollState) return;
      const body = document.body;
      const root = document.documentElement;
      const properties = ['position', 'top', 'left', 'right', 'width', 'overflow', 'padding-right'];
      this.scrollState = {
        x: window.scrollX, y: window.scrollY,
        body: properties.map(name => [name, body.style.getPropertyValue(name), body.style.getPropertyPriority(name)]),
        rootOverflow: root.style.getPropertyValue('overflow'),
        rootPriority: root.style.getPropertyPriority('overflow')
      };
      const scrollbar = window.innerWidth - root.clientWidth;
      const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
      root.classList.add('tg-menu-open');
      root.style.setProperty('overflow', 'hidden');
      body.style.position = 'fixed';
      body.style.top = `${-this.scrollState.y}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = 'auto';
      body.style.overflow = 'hidden';
      if (scrollbar > 0) body.style.paddingRight = `${padding + scrollbar}px`;
    }

    unlockScroll() {
      if (!this.scrollState) return;
      const state = this.scrollState;
      this.scrollState = null;
      const root = document.documentElement;
      state.body.forEach(([name, value, priority]) => {
        if (value) document.body.style.setProperty(name, value, priority);
        else document.body.style.removeProperty(name);
      });
      if (state.rootOverflow) root.style.setProperty('overflow', state.rootOverflow, state.rootPriority);
      else root.style.removeProperty('overflow');
      // "instant" avoids interaction with a theme's global smooth scrolling.
      window.scrollTo({ left: state.x, top: state.y, behavior: 'instant' });
      root.classList.remove('tg-menu-open');
    }
  }
  customElements.define('tagomago-navigation', TagomagoNavigation);
})();
